import React, { useEffect, useState } from 'react';
import { reducedMotion } from './effects';

// The reward for poking at things.
//
// Reachable three ways: the konami code, `panic` in the shell, and the one
// command everybody tries first. It prints a real-shaped oxidation fault over
// the whole page, then reboots back into the site.
const TRACE = [
  'oxide_kernel::sched::preempt::schedule+0x1f2/0x480',
  'oxide_kernel::syscalls::dispatch::enter+0x9c/0x310',
  'oxide_kernel::mm::vmalloc::map_pages+0x44/0x1a0',
  'oxide_drivers::virtio::blk::submit+0x18/0xc0',
  'oxide_shared::sync::spin::lock_irqsave+0x2a/0x60',
  'oxide_arch::aarch64::entry::el0_sync+0x140/0x1c0',
];

export default function Panic({ reason, stats, onDismiss }) {
  const [shown, setShown] = useState(0);

  const lines = [
    `Kernel panic - not syncing: oxidation fault at 0xffff_8000_0dead_beef`,
    `caused by: ${reason}`,
    '',
    `CPU: 3 PID: 1 Comm: init Not tainted 4.0.0-oxide #${stats.commits}`,
    `Hardware name: virtio (arm64) / oxide-vm`,
    '',
    'Call trace:',
    ...TRACE.map((frame) => ` [<ffffffc0100${Math.floor(Math.random() * 0xfffff).toString(16).padStart(5, '0')}>] ${frame}`),
    '',
    `${stats.unsafeBlocks.toLocaleString('en-US')} unsafe blocks reviewed. This was not one of them.`,
    '',
    '---[ end trace 0000000000000000 ]---',
    '',
    'press any key to reboot',
  ];

  useEffect(() => {
    if (reducedMotion()) { setShown(lines.length); return; }
    const timer = setInterval(() => setShown((at) => {
      if (at >= lines.length) { clearInterval(timer); return at; }
      return at + 1;
    }), 55);
    return () => clearInterval(timer);
  }, [reason]);

  useEffect(() => {
    // Not immediately: the keypress or click that caused the panic is still
    // bubbling toward the window, and a listener added mid-dispatch still
    // receives it — so a panic wired up on the spot dismisses itself with the
    // same keystroke that opened it. The pause also stops a reader who was
    // mid-sentence from wiping the screen before they have seen it.
    const leave = () => onDismiss();
    const arm = setTimeout(() => {
      addEventListener('keydown', leave);
      addEventListener('pointerdown', leave);
    }, 350);
    return () => {
      clearTimeout(arm);
      removeEventListener('keydown', leave);
      removeEventListener('pointerdown', leave);
    };
  }, [onDismiss]);

  // The colour is the reading order: what broke, where it broke, and the way
  // out. A panic printed in one flat grey is a wall of text nobody parses.
  const tone = (line) => {
    if (line.startsWith('Kernel panic') || line.startsWith('caused by')) return 'head';
    if (line.startsWith(' [<')) return 'trace';
    if (line.startsWith('press any key')) return 'exit';
    return 'out';
  };

  return (
    <div className="ox-panic" role="alertdialog" aria-label="Kernel panic">
      <div className="ox-panic__scan" aria-hidden="true" />
      <div className="ox-panic__body">
        {lines.slice(0, shown).map((line, index) => (
          <div className={`ox-panic__line ox-panic__line--${tone(line)}`} key={index}>{line || '\u00a0'}</div>
        ))}
        <span className="ox-panic__caret">▍</span>
      </div>
    </div>
  );
}
